
<template>
  <v-row>
    <v-col cols="12">
      <base-card>
        <v-card-title class="p-b-0">
          Third-Party Channels: Edit Metadata
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col
              cols="12"
              sm="12"
              md="12"
            >
              <hot-table
                v-if="!loading"
                :data="channels"
                :row-headers="true"
                :col-headers="true"
                :settings="hotSettings"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            @click="updatePack()"
          >
            <v-icon left>
              mdi-content-save
            </v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </base-card>
    </v-col>
  </v-row>
</template>
<script>
  import { processThirdPartyChannel } from '@/store/converters'
  import { HotTable } from '@handsontable/vue'
  import { registerAllModules } from 'handsontable/registry'
  import 'handsontable/dist/handsontable.full.css'
  import { textRenderer } from 'handsontable/renderers/textRenderer'

  registerAllModules()

  const nameValidator = (value, callback) => {
    setTimeout(() => {
      if (value && value.length >= 3) {
        // eslint-disable-next-line standard/no-callback-literal
        callback(true)
      } else {
        // eslint-disable-next-line standard/no-callback-literal
        callback(false)
      }
    }, 1000)
  }

  export default {
    metaInfo: {
      title: 'Edit Third Party Channels',
    },
    components: {
      HotTable,
    },
    data: () => ({
      hotSettings: {
        width: '100%',
        colHeaders: ['Channel Name', 'Graphics URL', 'Short Description', 'Long Description', 'Genres', 'Package', 'Company', 'Rating', 'XMLTV URL', 'Where to View', 'Per Deal Approval', 'Language', 'US Privacy', 'IAB Category', 'COPPA', 'Content ID'],
        rowHeights: 35,
        manualColumnResize: true,
        columnSorting: true,
        fixedColumnsStart: 1,
        height: '500px',
        licenseKey: 'non-commercial-and-evaluation',
        columns: [
          {
            data: 'name',
            width: 100,
            validator: nameValidator,
            allowInvalid: false,
          },
          {
            data: 'graphicsURL',
            width: 150,
            renderer (instance, td, row, col, prop, value, cellProperties) {
              const escaped = `${value}`
              if (escaped.indexOf('http') === 0) {
                const newlink = document.createElement('a')
                newlink.href = value
                newlink.innerText = value
                newlink.target = '_blank'

                newlink.addEventListener('mousedown', event => {
                  event.preventDefault()
                })

                td.innerText = ''
                td.appendChild(newlink)
              } else {
                textRenderer.apply(this, arguments)
              }

              return td
            },
          },
          {
            data: 'shortDescription',
            width: 200,
          },
          {
            data: 'longDescription',
            width: 250,
          },
          {
            data: 'genresStr',
            width: 100,
            readOnly: true,
          },
          {
            data: 'targetPackage',
            width: 100,
            type: 'dropdown',
            source: ['English', 'LATAM', 'Indian', 'African English'],
          },
          {
            data: 'company',
            width: 100,
          },
          {
            data: 'rating',
            width: 70,
            type: 'dropdown',
            source: ['TVY', 'TVY7', 'TVG', 'TV14', 'TVPG', 'TVMA'],
          },
          {
            data: 'xmltvURL',
            width: 150,
            renderer (instance, td, row, col, prop, value, cellProperties) {
              const escaped = `${value}`
              if (escaped.indexOf('http') === 0) {
                const newlink = document.createElement('a')
                newlink.href = value
                newlink.innerText = value
                newlink.target = '_blank'

                newlink.addEventListener('mousedown', event => {
                  event.preventDefault()
                })

                td.innerText = ''
                td.appendChild(newlink)
              } else {
                textRenderer.apply(this, arguments)
              }

              return td
            },
          },
          {
            data: 'whereToView',
            width: 150,
            renderer (instance, td, row, col, prop, value, cellProperties) {
              const escaped = `${value}`
              if (escaped.indexOf('http') === 0) {
                const newlink = document.createElement('a')
                newlink.href = value
                newlink.innerText = value
                newlink.target = '_blank'

                newlink.addEventListener('mousedown', event => {
                  event.preventDefault()
                })

                td.innerText = ''
                td.appendChild(newlink)
              } else {
                textRenderer.apply(this, arguments)
              }

              return td
            },
          },
          {
            data: 'perDealApproval',
            width: 100,
            type: 'dropdown',
            source: ['Per Deal Approval'],
          },
          {
            data: 'lang',
            width: 100,
            type: 'dropdown',
            source: ['en', 'es', 'pt', 'Hindi', 'French', 'German', 'Italian', 'Japanese', 'Chinese', 'Turkish'],
          },
          {
            data: 'USPrivacy',
            width: 70,
          },
          {
            data: 'IABCategory',
            width: 70,
          },
          {
            data: 'coppa',
            type: 'numeric',
            width: 70,
          },
          {
            data: 'contentId',
            width: 70,
          },
        ],
      },
      channels: [],
    }),
    computed: {
      loading () {
        return this.$store.getters.isDataLoaded
      },
    },
    created () {
      this.loadData()
    },
    methods: {
      loadData () {
        this.$store.commit('setPageLoader')

        const args = {
          $expand: '*',
        }
        const channelReq = this.$store.dispatch('thirdPartyChannels', args)

        return Promise.all([channelReq])
          .then((res) => {
            const channels = res[0] || []
            const processed = channels.map((ch) => processThirdPartyChannel(ch))
            if (!processed) return
            for (let i = 0; i < processed.length; i += 1) {
              const ch = processed[i]
              const genres = []
              for (let j = 0; j < ch.genres.length; j += 1) {
                genres.push(ch.genres[j].name)
              }
              ch.genresStr = genres.join(', ')
            }
            this.channels = processed
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
            // this.$router.push({ name: 'ChannelsPage' })
          })
          .finally(() => this.$store.commit('resolvePageLoader'))
      },
      updatePack () {
        this.$store.commit('setPageLoader')

        const data = this.channels

        this.$store.dispatch('bulkUpdateThirdPartyChannel', data)
          .then(() => {
            this.$store.commit('showSnackbar', {
              text: 'The channels has been successfully updated',
              timeout: 6000,
              color: 'success',
            })
          })
          .catch((errorMsg) => {
            this.$store.commit('showSnackbar', {
              text: errorMsg,
              timeout: 6000,
              color: 'error',
            })
          })
          .finally(() => {
            this.$store.commit('resolvePageLoader')
          })
      },
    },
  }
</script>

<style>
@media (min-width: 1200px) {
  .container {
    max-width: 1320px !important;
  }
}
</style>
